import { useEffect, useRef, useState, type KeyboardEvent, type PointerEvent, type ReactNode } from "react";
import { Pipette, RotateCcw } from "lucide-react";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { t } from "@/i18n/id";
import { cn } from "@/lib/cn";
import { hexToHsv, hsvToHex, isDark, normalizeHex, pushRecent, readRecent, type Hsv } from "./color";

export interface Swatch {
  hex: string;
  label: string;
}

type EyeDropperCtor = new () => { open: () => Promise<{ sRGBHex: string }> };

/**
 * A colour slot: the trigger is the colour itself (label + hex in mono), the popover a saturation/
 * brightness plane, a hue strip, a hex box, the site's own colours and recently used ones.
 * `onChange` fires while dragging (pass an undo key upstream so a drag is one step).
 */
export function ColorField({
  label,
  value,
  onChange,
  swatches = [],
  overridden = false,
  onReset,
  size = "tile",
  retainFocus = false,
  className,
}: {
  label: string;
  value: string;
  onChange: (hex: string) => void;
  /** Quick picks: the site's palette. */
  swatches?: Swatch[];
  /** The value differs from the design's (shows a dot and the reset button). */
  overridden?: boolean;
  onReset?: () => void;
  /** tile: big swatch card; chip: inline row; dot: round swatch for the dark text toolbar; swatch: a circle on a light surface. */
  size?: "tile" | "chip" | "dot" | "swatch";
  /** Keep an in-place text caret: the menu does not steal focus, and closing it returns there. */
  retainFocus?: boolean;
  className?: string;
}) {
  const [open, setOpen] = useState(false);
  const dark = isDark(value);
  return (
    <Popover
      open={open}
      onOpenChange={(o) => {
        setOpen(o);
        if (!o) pushRecent(value);
      }}
    >
      <PopoverTrigger asChild>
        {size === "tile" ? (
          <button
            type="button"
            aria-label={`${label}: ${value}`}
            className={cn(
              "group relative flex h-[4.5rem] min-w-0 flex-col justify-between overflow-hidden rounded-xl p-2 text-left ring-1 ring-slate-900/10 transition outline-none ring-inset hover:-translate-y-px hover:shadow-[0_6px_16px_rgb(15_23_42/0.12)] focus-visible:ring-2 focus-visible:ring-primary data-[state=open]:ring-2 data-[state=open]:ring-primary",
              dark ? "text-white" : "text-slate-900",
              className,
            )}
            style={{ background: value }}
          >
            <span className="flex items-center gap-1 text-[11px] font-semibold tracking-wide uppercase opacity-80">
              {label}
              {overridden ? <span className={cn("size-1.5 rounded-full", dark ? "bg-white" : "bg-slate-900")} aria-hidden="true" /> : null}
            </span>
            <span className="font-mono text-[11px] opacity-70">{value.slice(1)}</span>
          </button>
        ) : size === "swatch" ? (
          <button
            type="button"
            aria-label={`${label}: ${value}`}
            title={label}
            className={cn("size-8 shrink-0 rounded-full ring-1 ring-slate-900/10 ring-inset outline-none transition hover:scale-105 focus-visible:ring-2 focus-visible:ring-primary data-[state=open]:ring-2 data-[state=open]:ring-primary data-[state=open]:ring-offset-2", className)}
            style={{ background: value }}
          />
        ) : size === "dot" ? (
          <button
            type="button"
            aria-label={`${label}: ${value}`}
            title={label}
            className={cn("tb-btn grid size-8 place-items-center rounded-lg outline-none hover:bg-white/10 focus-visible:ring-2 focus-visible:ring-white/70 data-[state=open]:bg-white/15", className)}
          >
            <span className="size-[18px] rounded-full ring-2 ring-white/80" style={{ background: value }} />
          </button>
        ) : (
          <button
            type="button"
            aria-label={`${label}: ${value}`}
            className={cn(
              "inline-flex h-9 min-w-0 items-center gap-2 rounded-lg bg-white pr-2.5 pl-1.5 text-[13px] font-medium text-slate-700 ring-1 ring-line transition outline-none hover:ring-slate-300 focus-visible:ring-2 focus-visible:ring-primary data-[state=open]:ring-2 data-[state=open]:ring-primary",
              className,
            )}
          >
            <span className="size-6 shrink-0 rounded-md ring-1 ring-slate-900/10 ring-inset" style={{ background: value }} />
            <span className="truncate">{label}</span>
            <span className="ml-auto font-mono text-[11px] text-slate-400">{value.slice(1)}</span>
          </button>
        )}
      </PopoverTrigger>
      {/* Above the phone's docked text bar (z-60) when opened from its style row. */}
      <PopoverContent
        align="start"
        className="w-[17.5rem] p-3 z-[70]!"
        onOpenAutoFocus={retainFocus ? (e) => e.preventDefault() : undefined}
        onCloseAutoFocus={
          retainFocus
            ? (e) => {
                const ta = document.querySelector<HTMLTextAreaElement>(".baze-inline-editor textarea, [data-docked-text]");
                if (!ta) return;
                e.preventDefault();
                ta.focus({ preventScroll: true });
              }
            : undefined
        }
        onPointerDown={
          retainFocus
            ? (e) => {
                const node = e.target instanceof Element ? e.target : null;
                if (!node?.closest("input, textarea, label")) e.preventDefault();
              }
            : undefined
        }
      >
        <Picker value={value} onChange={onChange} swatches={swatches} label={label} footer={overridden && onReset ? <ResetButton onReset={onReset} /> : null} />
      </PopoverContent>
    </Popover>
  );
}

function ResetButton({ onReset }: { onReset: () => void }) {
  return (
    <button type="button" onClick={onReset} className="inline-flex items-center gap-1 text-xs font-semibold text-slate-500 hover:text-slate-900">
      <RotateCcw className="size-3" aria-hidden="true" />
      {t.editor.style.useDesign}
    </button>
  );
}

function Picker({ value, onChange, swatches, label, footer }: { value: string; onChange: (hex: string) => void; swatches: Swatch[]; label: string; footer: ReactNode }) {
  // Hue is kept apart from the hex so dragging through grey/black does not snap the hue strip to red.
  const [hsv, setHsv] = useState<Hsv>(() => hexToHsv(value));
  const [draft, setDraft] = useState(value.slice(1));
  const [recent] = useState(readRecent);
  const last = useRef(value);
  useEffect(() => {
    if (value === last.current) return;
    last.current = value;
    const next = hexToHsv(value);
    setHsv((cur) => (next.s === 0 ? { ...next, h: cur.h } : next));
    setDraft(value.slice(1));
  }, [value]);

  const commit = (next: Hsv) => {
    setHsv(next);
    const hex = hsvToHex(next);
    last.current = hex;
    setDraft(hex.slice(1));
    onChange(hex);
  };
  const pick = (hex: string) => {
    const h = hexToHsv(hex);
    setHsv(h.s === 0 ? { ...h, h: hsv.h } : h);
    last.current = hex;
    setDraft(hex.slice(1));
    onChange(hex);
  };

  const plane = useDrag((x, y) => commit({ h: hsv.h, s: x, v: 1 - y }));
  const hue = useDrag((x) => commit({ ...hsv, h: Math.min(359.9, x * 360) }));
  const eyeDropper = typeof window !== "undefined" ? (window as unknown as { EyeDropper?: EyeDropperCtor }).EyeDropper : undefined;

  const nudge = (e: KeyboardEvent, axis: "plane" | "hue") => {
    const step = e.shiftKey ? 0.1 : 0.02;
    const k = e.key;
    if (axis === "hue" && (k === "ArrowLeft" || k === "ArrowRight")) {
      e.preventDefault();
      commit({ ...hsv, h: (hsv.h + (k === "ArrowRight" ? 1 : -1) * step * 360 + 360) % 360 });
    }
    if (axis === "plane" && k.startsWith("Arrow")) {
      e.preventDefault();
      const clamp = (n: number) => Math.max(0, Math.min(1, n));
      commit({
        h: hsv.h,
        s: clamp(hsv.s + (k === "ArrowRight" ? step : k === "ArrowLeft" ? -step : 0)),
        v: clamp(hsv.v + (k === "ArrowUp" ? step : k === "ArrowDown" ? -step : 0)),
      });
    }
  };

  return (
    <div className="space-y-3">
      <div
        {...plane}
        role="slider"
        tabIndex={0}
        aria-label={t.editor.style.picker.plane(label)}
        aria-valuetext={`${Math.round(hsv.s * 100)}% / ${Math.round(hsv.v * 100)}%`}
        onKeyDown={(e) => nudge(e, "plane")}
        className="relative h-36 cursor-crosshair touch-none rounded-xl outline-none focus-visible:ring-2 focus-visible:ring-primary focus-visible:ring-offset-2"
        style={{ background: `linear-gradient(to top, #000, transparent), linear-gradient(to right, #fff, hsl(${hsv.h} 100% 50%))` }}
      >
        <span
          className="pointer-events-none absolute size-4 -translate-x-1/2 -translate-y-1/2 rounded-full border-2 border-white shadow-[0_0_0_1px_rgb(0_0_0/0.25),0_2px_6px_rgb(0_0_0/0.3)]"
          style={{ left: `${hsv.s * 100}%`, top: `${(1 - hsv.v) * 100}%`, background: hsvToHex(hsv) }}
        />
      </div>
      <div
        {...hue}
        role="slider"
        tabIndex={0}
        aria-label={t.editor.style.picker.hue}
        aria-valuemin={0}
        aria-valuemax={360}
        aria-valuenow={Math.round(hsv.h)}
        onKeyDown={(e) => nudge(e, "hue")}
        className="relative h-3.5 cursor-pointer touch-none rounded-full outline-none focus-visible:ring-2 focus-visible:ring-primary focus-visible:ring-offset-2"
        style={{ background: "linear-gradient(to right, #f00, #ff0 17%, #0f0 33%, #0ff 50%, #00f 67%, #f0f 83%, #f00)" }}
      >
        <span
          className="pointer-events-none absolute top-1/2 h-5 w-2.5 -translate-x-1/2 -translate-y-1/2 rounded-full border-2 border-white shadow-[0_0_0_1px_rgb(0_0_0/0.25)]"
          style={{ left: `${(hsv.h / 360) * 100}%`, background: `hsl(${hsv.h} 100% 50%)` }}
        />
      </div>
      <div className="flex items-center gap-2">
        <label className="flex h-9 min-w-0 flex-1 items-center gap-1 rounded-lg bg-slate-50 px-2.5 ring-1 ring-line focus-within:ring-2 focus-within:ring-primary">
          <span className="font-mono text-sm text-slate-400">#</span>
          <input
            value={draft}
            aria-label={t.editor.style.picker.hex}
            spellCheck={false}
            maxLength={7}
            onChange={(e) => {
              setDraft(e.target.value.replace(/^#/, ""));
              const hex = normalizeHex(e.target.value);
              if (hex && e.target.value.replace(/^#/, "").length === 6) pick(hex);
            }}
            onBlur={() => {
              const hex = normalizeHex(draft);
              if (hex) pick(hex);
              else setDraft(value.slice(1));
            }}
            className="w-full min-w-0 bg-transparent font-mono text-sm text-slate-800 uppercase outline-none"
          />
        </label>
        {eyeDropper ? (
          <button
            type="button"
            aria-label={t.editor.style.picker.eyedropper}
            title={t.editor.style.picker.eyedropper}
            onClick={async () => {
              try {
                const res = await new eyeDropper().open();
                const hex = normalizeHex(res.sRGBHex);
                if (hex) pick(hex);
              } catch {
                /* cancelled */
              }
            }}
            className="grid size-9 shrink-0 place-items-center rounded-lg text-slate-600 ring-1 ring-line hover:bg-slate-50 hover:text-slate-900"
          >
            <Pipette className="size-4" aria-hidden="true" />
          </button>
        ) : null}
      </div>
      <SwatchRow title={t.editor.style.picker.site} swatches={swatches} value={value} onPick={pick} />
      <SwatchRow title={t.editor.style.picker.recent} swatches={recent.map((hex) => ({ hex, label: hex }))} value={value} onPick={pick} />
      {footer ? <div className="border-t border-line pt-2.5">{footer}</div> : null}
    </div>
  );
}

function SwatchRow({ title, swatches, value, onPick }: { title: string; swatches: Swatch[]; value: string; onPick: (hex: string) => void }) {
  const unique = swatches.filter((s, i) => swatches.findIndex((x) => x.hex.toUpperCase() === s.hex.toUpperCase()) === i);
  if (!unique.length) return null;
  return (
    <div>
      <p className="mb-1.5 text-[11px] font-bold tracking-wide text-slate-400 uppercase">{title}</p>
      <div className="flex flex-wrap gap-1.5">
        {unique.map((s) => (
          <button
            key={s.hex}
            type="button"
            title={s.label}
            aria-label={s.label}
            aria-pressed={s.hex.toUpperCase() === value.toUpperCase()}
            onClick={() => onPick(s.hex.toUpperCase())}
            className="size-7 rounded-lg ring-1 ring-slate-900/10 transition ring-inset hover:scale-110 aria-pressed:ring-2 aria-pressed:ring-primary aria-pressed:ring-offset-2"
            style={{ background: s.hex }}
          />
        ))}
      </div>
    </div>
  );
}

/** Pointer drag over an element → normalised (x, y) in 0…1. */
function useDrag(onMove: (x: number, y: number) => void) {
  const move = (e: PointerEvent<HTMLElement>) => {
    const r = e.currentTarget.getBoundingClientRect();
    const clamp = (n: number) => Math.max(0, Math.min(1, n));
    onMove(clamp((e.clientX - r.left) / r.width), clamp((e.clientY - r.top) / r.height));
  };
  return {
    onPointerDown: (e: PointerEvent<HTMLElement>) => {
      e.currentTarget.setPointerCapture(e.pointerId);
      move(e);
    },
    onPointerMove: (e: PointerEvent<HTMLElement>) => {
      if (e.currentTarget.hasPointerCapture(e.pointerId)) move(e);
    },
  };
}
